import { Field, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { debounce } from '@mui/material/utils';

import { Autocomplete } from 'common/components/FormField';
import {
    InputOptionUsersDocument,
    ISortDirection,
    IUserSortBy,
} from 'graphql/types';
import { TObjectFilterValue } from 'common/types';

interface IProps {
    label?: string;
    onChange?: (name: string, value: TObjectFilterValue | null) => void;
}

const BASE_QUERY_VARS = {
    first: 50,
    sortBy: { field: IUserSortBy.Name, direction: ISortDirection.Asc },
};

export const UserSelectField = ({
    label,
    onChange,
    ...other
}: IProps & FieldProps) => {
    const [inputValue, setInputValue] = useState('');
    const [translate] = useTranslation();

    const [fetchUsers, { data, loading, called }] = useLazyQuery(
        InputOptionUsersDocument
    );

    const users =
        data?.paginatedUsers?.edges.map((edge) => edge?.node).filter(Boolean) ||
        [];

    const value: TObjectFilterValue | null = other.field.value || null;

    // Make sure the selected value is present in the options so the
    // Autocomplete can render its label, e.g. after restoring from the URL.
    const options: TObjectFilterValue[] = useMemo(() => {
        const base = users.map(({ id, name }) => ({ id, name }));

        if (value && !base.some((option) => option.id === value.id)) {
            return [...base, value];
        }

        return base;
    }, [users, value]);

    const {
        form: { setFieldValue },
    } = other;

    const handleChange = (
        name: string,
        newValue: TObjectFilterValue | null
    ) => {
        onChange?.(name, newValue);
        setFieldValue(name, newValue);
    };

    const fetch = useMemo(
        () =>
            debounce((query: string) => {
                fetchUsers({
                    variables: { ...BASE_QUERY_VARS, q: query || undefined },
                });
            }, 400),
        []
    );

    // Search for results when the input value changes
    useEffect(() => {
        // The first query is done on open, so we avoid calling it twice
        if (!called) return;

        fetch(inputValue);
    }, [inputValue, fetch]);

    const handleOpen = () => {
        if (users.length) return;

        fetchUsers({
            variables: { ...BASE_QUERY_VARS, q: inputValue || undefined },
        });
    };

    return (
        <Field
            component={Autocomplete}
            // Disable client-side filtering; the server already filters on `q`.
            filterOptions={(filteredOptions: TObjectFilterValue[]) =>
                filteredOptions
            }
            getOptionLabel={(option: TObjectFilterValue) => option.name}
            InputProps={{ label }}
            isOptionEqualToValue={(
                option: TObjectFilterValue,
                selected: TObjectFilterValue
            ) => option.id === selected.id}
            loading={loading}
            loadingText={translate('loadingDots')}
            noOptionsText={translate('noOptionsText.users')}
            options={options}
            onChange={handleChange}
            onInputChange={(_event: unknown, newInputValue: string) =>
                setInputValue(newInputValue)
            }
            onOpen={handleOpen}
            {...other}
        />
    );
};
